import { Spin } from "antd";
import React, { useEffect, useState } from "react";

export default function Airtime() {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // Comme Organisations.js
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [message, setMessage] = useState({ text: "", type: "" });
  const transactionsPerPage = 15;

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/transactions`
        );

        if (!res.ok)
          throw new Error("Erreur lors de la récupération des données");

        const data = await res.json();

        // Désérialisation éventuelle des champs string
        const formattedData = data.map((transaction) => {
          if (
            transaction.paymentDetails &&
            typeof transaction.paymentDetails === "string"
          ) {
            try {
              transaction.paymentDetails = JSON.parse(
                transaction.paymentDetails
              );
            } catch (error) {
              console.error(
                "Erreur de désérialisation de paymentDetails",
                error
              );
            }
          }

          if (transaction.details) {
            try {
              if (typeof transaction.details.senderDetails === "string") {
                transaction.details.senderDetails = JSON.parse(
                  transaction.details.senderDetails
                );
              }
              if (typeof transaction.details.receiveDetails === "string") {
                transaction.details.receiveDetails = JSON.parse(
                  transaction.details.receiveDetails
                );
              }
            } catch (error) {
              console.error("Erreur de désérialisation des details", error);
            }
          }
          return transaction;
        });

        // 🔥 FILTRER UNIQUEMENT LES TRANSACTIONS AIRTIME
        const airtimeTransactions = formattedData.filter(
          (transaction) => transaction.transactionType === "airtime"
        );

        setTransactions(airtimeTransactions);
        setFilteredTransactions(airtimeTransactions);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des transactions:",
          error
        );
        showMessage("Erreur lors du chargement des données", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const fetchTransactionDetails = async (reference) => {
    try {
      setDetailsLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/transactions/${reference}`
      );

      if (!res.ok) throw new Error("Transaction introuvable");

      const responseData = await res.json();

      // Vérifier si la réponse est un tableau ou un objet unique
      let data;
      if (Array.isArray(responseData)) {
        // Si c'est un tableau, chercher la transaction airtime
        const airtimeTransaction = responseData.find(
          (transaction) => transaction.transactionType === "airtime"
        );

        // 🔥 TRIER PAR DATE DE CRÉATION (PLUS RÉCENTE EN PREMIER)
        const sortedTransactions = airtimeTransaction.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB - dateA; // Ordre décroissant (plus récent en premier)
        });
        if (!airtimeTransaction) {
          throw new Error(
            "Aucune transaction airtime trouvée avec cette référence"
          );
        }
        data = sortedTransactions;
      } else {
        // Si c'est un objet unique, vérifier que c'est bien airtime
        if (responseData.transactionType !== "airtime") {
          throw new Error("Cette transaction n'est pas de type airtime");
        }
        data = responseData;
      }

      // Désérialisation des champs JSON si nécessaire
      if (data.paymentDetails && typeof data.paymentDetails === "string") {
        try {
          data.paymentDetails = JSON.parse(data.paymentDetails);
        } catch (error) {
          console.error("Erreur de désérialisation de paymentDetails", error);
        }
      }

      if (data.details) {
        try {
          if (typeof data.details.senderDetails === "string") {
            data.details.senderDetails = JSON.parse(data.details.senderDetails);
          }
          if (typeof data.details.receiveDetails === "string") {
            data.details.receiveDetails = JSON.parse(
              data.details.receiveDetails
            );
          }
        } catch (error) {
          console.error("Erreur de désérialisation des details", error);
        }
      }

      setTransactionDetails(data);
      setModalVisible(true);
    } catch (error) {
      console.error("Erreur lors du chargement des détails:", error);
      showMessage(`Erreur: ${error.message}`, "error");
      setTransactionDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const getProviderImage = (transaction) => {
    // Affiche l'icône carte uniquement si service === "credit-card"
    if (transaction.paymentDetails?.service?.toLowerCase() === "credit-card") {
      return (
        <img
          src="/v-c.webp"
          alt="credit-card"
          style={{ height: "32px", width: "32px", objectFit: "fill" }}
        />
      );
    }

    const provider =
      transaction.paymentDetails?.provider ||
      transaction.details?.senderDetails?.provider;

    const providerImages = {
      wave: "/wave.png",
      orange: "/orange.jpg",
      mtn: "/mtn.png",
      moov: "/moov.png",
      air: "/air.png",
    };

    if (provider && providerImages[provider]) {
      return (
        <img
          src={providerImages[provider]}
          alt={provider}
          style={{ height: "32px", width: "32px" }}
        />
      );
    }

    if (transaction.transactionType === "airtime") {
      return (
        <img
          src="/air.png"
          alt="airtime"
          style={{ height: "32px", width: "32px" }}
        />
      );
    }

    return null;
  };

  // 👉 Nouvelle fonction pour le fournisseur
  const getFournisseurInfo = (transaction) => {
    if (transaction.paymentDetails?.phone_number) {
      return transaction.paymentDetails.phone_number;
    }
    if (transaction.details?.senderDetails?.phone_number) {
      return transaction.details.senderDetails.phone_number;
    }
    if (transaction?.organisation?.phone) {
      return transaction.organisation.phone;
    }
    return "N/A";
  };

  // 👉 Nouvelle fonction pour l'organisation
  const getOrganisationInfo = (transaction) => {
    return transaction.organisation?.name || "N/A";
  };

  // Fonction pour extraire les informations de l'expéditeur
  const getSenderInfo = (transaction) => {
    // Pour les transferts, on a senderDetails
    if (transaction.details?.senderDetails) {
      return transaction.details.senderDetails.phone_number || "N/A";
    }

    // Pour les autres transactions (recharge, paiement), l'expéditeur est dans paymentDetails
    if (transaction.paymentDetails?.phone_number) {
      return transaction.paymentDetails.phone_number;
    }

    // Fallback sur phoneNumber

    if (transaction.phoneNumber) {
      return transaction.phoneNumber;
    }

    return "N/A";
  };

  // 🔥 FILTRAGE AMÉLIORÉ (recherche par numéro, montant, référence + filtre fournisseur/service)
  useEffect(() => {
    let filtered = transactions;

    if (searchTerm) {
      filtered = filtered.filter((transaction) => {
        const fournisseur = getFournisseurInfo(transaction);
        const organisation = getOrganisationInfo(transaction);
        const reference = transaction.reference || "";
        const montant = transaction.amount ? transaction.amount.toString() : "";
        const phoneNumber = transaction.phoneNumber || "";

        return (
          fournisseur.toLowerCase().includes(searchTerm.toLowerCase()) ||
          organisation.toLowerCase().includes(searchTerm.toLowerCase()) ||
          reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
          montant.includes(searchTerm) ||
          phoneNumber.includes(searchTerm)
        );
      });
    }

    // Filtre par fournisseur / service
    if (statusFilter !== "all") {
      filtered = filtered.filter((transaction) => {
        const provider =
          (transaction.paymentDetails && transaction.paymentDetails.provider) ||
          (transaction.details && transaction.details.senderDetails.provider);

        const service =
          transaction.paymentDetails && transaction.paymentDetails.service;

        if (statusFilter === "credit-card") {
          return service && service.toLowerCase() === "credit-card";
        }

        return provider && provider.toLowerCase() === statusFilter;
      });
    }

    setFilteredTransactions(filtered);
    setCurrentPage(1);
  }, [transactions, searchTerm, statusFilter]);

  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(
    indexOfFirstTransaction,
    indexOfLastTransaction
  );
  const totalPages = Math.ceil(
    filteredTransactions.length / transactionsPerPage
  );

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      {/* Message d'alerte */}
      {message.text && (
        <div
          style={{
            padding: "10px",
            marginBottom: "20px",
            borderRadius: "4px",
            backgroundColor: message.type === "error" ? "#ffebee" : "#e8f5e8",
            color: message.type === "error" ? "#c62828" : "#2e7d32",
            border: `1px solid ${
              message.type === "error" ? "#ef5350" : "#66bb6a"
            }`,
          }}
        >
          {message.text}
        </div>
      )}

      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ padding: "20px", borderBottom: "1px solid #f0f0f0" }}>
          <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "600" }}>
            Transactions Airtime
          </h2>

          {/* Barre de recherche et filtres (boutons comme Organisations.js) */}
          <div
            style={{
              marginTop: "20px",
              display: "flex",
              gap: "16px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ position: "relative" }}>
              <input
                type="text"
                placeholder="Rechercher par numéro, montant, référence..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: "8px 12px 8px 35px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                  width: "350px",
                  fontSize: "14px",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#999",
                }}
              >
                🔍
              </span>
            </div>

            {/* Boutons de filtre — simplifiés pour airtime uniquement */}
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => setStatusFilter("all")}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d9d9d9",
                  backgroundColor: statusFilter === "all" ? "#1890ff" : "white",
                  color: statusFilter === "all" ? "white" : "#666",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Tous
              </button>

              <button
                onClick={() => setStatusFilter("wave")}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d9d9d9",
                  backgroundColor:
                    statusFilter === "wave" ? "#1890ff" : "white",
                  color: statusFilter === "wave" ? "white" : "#666",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Wave
              </button>

              <button
                onClick={() => setStatusFilter("moov")}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d9d9d9",
                  backgroundColor:
                    statusFilter === "moov" ? "#1890ff" : "white",
                  color: statusFilter === "moov" ? "white" : "#666",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Moov
              </button>

              <button
                onClick={() => setStatusFilter("orange")}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d9d9d9",
                  backgroundColor:
                    statusFilter === "orange" ? "#1890ff" : "white",
                  color: statusFilter === "orange" ? "white" : "#666",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Orange
              </button>

              <button
                onClick={() => setStatusFilter("mtn")}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d9d9d9",
                  backgroundColor: statusFilter === "mtn" ? "#1890ff" : "white",
                  color: statusFilter === "mtn" ? "white" : "#666",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                MTN
              </button>

              <button
                onClick={() => setStatusFilter("credit-card")}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d9d9d9",
                  backgroundColor:
                    statusFilter === "credit-card" ? "#1890ff" : "white",
                  color: statusFilter === "credit-card" ? "white" : "#666",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Carte bancaire
              </button>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div style={{ fontSize: "18px", color: "#666" }}>
                <Spin />
              </div>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ backgroundColor: "#fafafa" }}>
                <tr>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    RÉFÉRENCE
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    MONTANT
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    TYPE DE TRANSACTION
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    FRAIS
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    STATUT
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    DEBITEUR
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    MARCHAND
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    OPERATEUR
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    DATE DE CRÉATION
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontWeight: 600,
                    }}
                  >
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentTransactions.map((transaction) => (
                  <tr
                    key={transaction.transactionId}
                    style={{ borderBottom: "1px solid #f0f0f0" }}
                  >
                    <td style={{ padding: "12px" }}>{transaction.reference}</td>
                    <td style={{ padding: "12px" }}>
                      <span>{transaction.amount}</span>&nbsp;<span>XOF</span>
                    </td>
                    <td style={{ padding: "12px" }}>
                      {transaction.transactionType}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {transaction.fees.amount} {transaction.fees.currency}
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          backgroundColor:
                            transaction.status === "success"
                              ? "#f6ffed"
                              : transaction.status === "failed"
                              ? "#fff2f0"
                              : "#fffbe6",
                          color:
                            transaction.status === "success"
                              ? "#52c41a"
                              : transaction.status === "failed"
                              ? "#ff4d4f"
                              : "#faad14",
                        }}
                      >
                        {transaction.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>
                      {getFournisseurInfo(transaction)}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {getOrganisationInfo(transaction)}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {getProviderImage(transaction)}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {new Date(transaction.createdAt).toUTCString()}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() =>
                          fetchTransactionDetails(transaction.reference)
                        }
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #1890ff",
                          backgroundColor: "white",
                          color: "#1890ff",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        Voir les détails
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Message si aucune transaction airtime */}
        {!loading && currentTransactions.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div style={{ fontSize: "16px", color: "#999" }}>
              {searchTerm || statusFilter !== "all"
                ? "Aucune transaction airtime trouvée avec ces critères"
                : "Aucune transaction airtime disponible"}
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 20px",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={{
                padding: "8px 16px",
                border: "1px solid #d9d9d9",
                backgroundColor: currentPage === 1 ? "#f5f5f5" : "white",
                color: currentPage === 1 ? "#999" : "#333",
                borderRadius: "4px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              Précédent
            </button>
            <span style={{ fontSize: "14px", color: "#666" }}>
              Page {currentPage} sur {totalPages} ({filteredTransactions.length}{" "}
              transactions airtime)
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{
                padding: "8px 16px",
                border: "1px solid #d9d9d9",
                backgroundColor:
                  currentPage === totalPages ? "#f5f5f5" : "white",
                color: currentPage === totalPages ? "#999" : "#333",
                borderRadius: "4px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              }}
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {/* Modal détails */}
      {modalVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "20px",
              maxWidth: "700px",
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}>
                Détails de la transaction
              </h3>
              <button
                onClick={() => setModalVisible(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#666",
                }}
              >
                ✕
              </button>
            </div>

            {detailsLoading ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <div style={{ fontSize: "16px", color: "#666" }}>
                  Chargement...
                </div>
              </div>
            ) : transactionDetails ? (
              <div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <div>
                    <strong>Référence :</strong>
                    <div>{transactionDetails.reference || "N/A"}</div>
                  </div>
                  <div>
                    <strong>Montant :</strong>
                    <div>
                      {transactionDetails.amount} {transactionDetails.currency}
                    </div>
                  </div>
                  <div>
                    <strong>Type de transaction :</strong>
                    <div>{transactionDetails.transactionType || "N/A"}</div>
                  </div>
                  <div>
                    <strong>Statut :</strong>
                    <div>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          backgroundColor:
                            transactionDetails.status === "success"
                              ? "#f6ffed"
                              : transactionDetails.status === "failed"
                              ? "#fff2f0"
                              : "#fffbe6",
                          color:
                            transactionDetails.status === "success"
                              ? "#52c41a"
                              : transactionDetails.status === "failed"
                              ? "#ff4d4f"
                              : "#faad14",
                        }}
                      >
                        {transactionDetails.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <strong>Raison de l'échec :</strong>
                    <div>{transactionDetails.failureReason || "Aucune"}</div>
                  </div>
                  <div>
                    <strong>Code pays :</strong>
                    <div>
                      {transactionDetails.paymentDetails?.country_code?.toUpperCase() ||
                        "N/A"}
                    </div>
                  </div>
                  <div>
                    <strong>Date de création :</strong>
                    <div>
                      {transactionDetails.createdAt
                        ? new Date(transactionDetails.createdAt).toUTCString()
                        : "N/A"}
                    </div>
                  </div>
                </div>

                {/* Détails de paiement */}
                {transactionDetails.paymentDetails && (
                  <>
                    <h4
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        marginBottom: "15px",
                        marginTop: "20px",
                      }}
                    >
                      Détails de paiement
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <strong>Service :</strong>
                        <div>
                          {transactionDetails.paymentDetails.service || "N/A"}
                        </div>
                      </div>
                      <div>
                        <strong>Fournisseur :</strong>
                        <div>
                          {transactionDetails.paymentDetails.provider || "N/A"}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Détails des comptes (pour les transferts) */}
                {transactionDetails.details && (
                  <>
                    <h4
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        marginBottom: "15px",
                        marginTop: "20px",
                      }}
                    >
                      Détails des comptes
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      {transactionDetails.details.senderDetails && (
                        <>
                          <div>
                            <strong>Compte débiteur :</strong>
                            <div>
                              {transactionDetails.details.senderDetails
                                .phone_number || "N/A"}
                            </div>
                          </div>
                          <div>
                            <strong>Fournisseur expéditeur :</strong>
                            <div>
                              {transactionDetails.details.senderDetails
                                .provider || "N/A"}
                            </div>
                          </div>
                        </>
                      )}
                      {transactionDetails.details.receiveDetails && (
                        <>
                          <div>
                            <strong>Compte bénéficiaire :</strong>
                            <div>
                              {transactionDetails.details.receiveDetails
                                .phone_number || "N/A"}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    {/* Détails des frais */}
                    {transactionDetails.fees && (
                      <>
                        <h4
                          style={{
                            fontSize: "18px",
                            fontWeight: "600",
                            marginBottom: "15px",
                            marginTop: "20px",
                          }}
                        >
                          Détails des frais
                        </h4>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "16px",
                            backgroundColor: "#fff7e6",
                            padding: "15px",
                            borderRadius: "6px",
                            border: "1px solid #ffd591",
                          }}
                        >
                          <div>
                            <strong>Montant des frais :</strong>
                            <div
                              style={{
                                color: "#ff6b35",
                                fontWeight: "500",
                                fontSize: "16px",
                              }}
                            >
                              {transactionDetails.fees.amount}{" "}
                              {transactionDetails.fees.currency}
                            </div>
                          </div>
                          <div>
                            <strong>Taux appliqué :</strong>
                            <div style={{ fontWeight: "500" }}>
                              {transactionDetails.fees.rate}%
                            </div>
                          </div>
                          <div>
                            <strong>ID des frais :</strong>
                            <div style={{ fontSize: "14px", color: "#666" }}>
                              {transactionDetails.fees.feesId}
                            </div>
                          </div>
                          <div>
                            <strong>Date de création :</strong>
                            <div style={{ fontSize: "14px" }}>
                              {new Date(
                                transactionDetails.fees.createdAt
                              ).toLocaleDateString("fr-FR", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                <div style={{ marginTop: "20px", textAlign: "right" }}>
                  <button
                    onClick={() => setModalVisible(false)}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #ff4d4f",
                      backgroundColor: "#ff4d4f",
                      color: "white",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            ) : (
              <p>Erreur lors du chargement des détails</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
